import { useState } from 'react';
import { Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAppDispatch } from '@reduxStore';
import { queueUncontrolledToast } from '@toast';
import { settlementsSlice, useFetchStatementsQuery } from '@api/Trinium/Endpoints/Settlements/slice';
import useCurrentTerminal from '@hooks/useCurrentTerminal';

/** Downloads the whole statement as one workbook, a sheet per tab, regardless of any grid filtering. */
export default function ExportSettlementButton({ statementId }: { statementId: number }) {
  const dispatch = useAppDispatch();
  const terminal = useCurrentTerminal();
  const { data: statements } = useFetchStatementsQuery({ terminalNumber: terminal.number });
  const [exporting, setExporting] = useState(false);

  const statement = statements?.find((candidate) => candidate.statementId === statementId);

  async function handleExport() {
    if (!statement) return;
    setExporting(true);

    // Served from the RTK cache for any tab already visited; only the unvisited ones hit the API.
    const request = { terminalNumber: terminal.number, statementId };
    const { endpoints } = settlementsSlice;
    const summaryQuery = dispatch(endpoints.fetchSettlementSummary.initiate(request));
    const loadsQuery = dispatch(endpoints.fetchSettlementLoads.initiate(request));
    const commissionsQuery = dispatch(endpoints.fetchCommissions.initiate(request));
    const deductionsQuery = dispatch(endpoints.fetchDeductions.initiate(request));
    const billingWeekQuery = dispatch(endpoints.fetchBillingWeek.initiate(request));
    const ytdQuery = dispatch(endpoints.fetchYtd.initiate(request));

    try {
      const [summary, loads, commissions, deductions, billingWeek, ytd] = await Promise.all([
        summaryQuery.unwrap(),
        loadsQuery.unwrap(),
        commissionsQuery.unwrap(),
        deductionsQuery.unwrap(),
        billingWeekQuery.unwrap(),
        ytdQuery.unwrap()
      ]);
      // Loaded on demand so the xlsx writer stays out of the page's initial bundle.
      const { exportSettlement } = await import('./exportSettlement');
      await exportSettlement({
        agent: { id: terminal.agtId, name: terminal.agtName },
        statement,
        summary,
        loads,
        commissions,
        deductions,
        billingWeek,
        ytd
      });
    } catch {
      dispatch(
        queueUncontrolledToast({
          type: 'message',
          message: 'Unable to export the settlement, if the problem persists please contact IT Support.',
          severity: 'error',
          autoCloseMs: 5000
        })
      );
    } finally {
      [summaryQuery, loadsQuery, commissionsQuery, deductionsQuery, billingWeekQuery, ytdQuery].forEach((query) => {
        query.unsubscribe();
      });
      setExporting(false);
    }
  }

  return (
    <Button
      variant='outlined'
      size='small'
      startIcon={<FileDownloadIcon />}
      loading={exporting}
      loadingPosition='start'
      disabled={!statement}
      onClick={() => {
        void handleExport();
      }}>
      Export
    </Button>
  );
}
